import { Text, mergeClasses } from '@fluentui/react-components';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PeopleCommunity16Regular } from '@fluentui/react-icons';
import {
    useMisconnectedCustomerInfoDialogStyles,
    useDataRowStyles,
    useReflightInfoStyles,
} from './MisconnectedCustomerInfoDialog.styles';
import { PassengerIcon } from '../../Icons/PassengerIcon';
import CustomerUpdatesDialog from '../components/CustomerUpdatesDialog/CustomerUpdatesDialog';
import {
    CustomerFlightTeam,
    CustomerRebookedFlight,
    CustomerWithStatuses,
} from '../../../model/customerWithUpdates';
import { fetchRebookedFlight } from '../../../store/features/customerUpdates/customerUpdates.api';
import { ICustomerNotificationActionContext } from '../../../store/features/customerUpdates/customerUpdates.types';
import { useCustomerUpdatesDialogResultCallbacks } from '../components/CustomerUpdatesDialog/useCustomerUpdatesDialogResultCallback';
import { useCustomerUpdatesDialogContentStyles } from '../components/CustomerUpdatesDialog/CustomerUpdatesDialog.styles';

interface CustomerBaggageInfoDialogProps {
    isFlightTeamBoard: boolean;
    open?: boolean;
    customerInfo: CustomerWithStatuses | null;
    onResultClick?: (context: ICustomerNotificationActionContext) => void;
    onOpenChange?: (open: boolean) => void;
}

const formatDateTime = (date: Date): string => {
    if (isNaN(date.getTime())) {
        return '';
    }

    const pad = (n: number) => n.toString().padStart(2, '0');
    const day = pad(date.getUTCDate());
    const month = pad(date.getUTCMonth() + 1);
    const hours = pad(date.getUTCHours());
    const minutes = pad(date.getUTCMinutes());
    return `${day}/${month} ${hours}:${minutes}`;
};

const isFutureDate = (date: Date | undefined): boolean => {
    if (!date || isNaN(date.getTime())) {
        return false;
    }

    const now = new Date();
    const tomorrowUTC = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)
    );

    return date.getTime() >= tomorrowUTC.getTime();
};

const DataRow = React.memo(
    ({
        label,
        value,
        rootClassName,
        labelClassName,
        valueClassName,
        vertical,
    }: {
        label: string;
        value: string | number | React.ReactElement | null | undefined;
        rootClassName?: string;
        labelClassName?: string;
        valueClassName?: string;
        vertical?: boolean;
    }) => {
        const styles = useDataRowStyles();

        return (
            <div
                className={mergeClasses(
                    styles.root,
                    rootClassName,
                    vertical ? styles.vertical : undefined
                )}
            >
                <Text
                    className={labelClassName}
                    style={{
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                    }}
                >
                    {label}
                </Text>
                <Text
                    className={mergeClasses(
                        styles.dataRowValue,
                        valueClassName
                    )}
                    style={{
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                    }}
                >
                    {value}
                </Text>
            </div>
        );
    }
);

const FlightInfo = React.memo(
    ({
        rebookedFlight,
        flightTeam,
        className,
    }: {
        rebookedFlight: CustomerRebookedFlight;
        flightTeam: CustomerFlightTeam;
        className?: string;
    }) => {
        const { t } = useTranslation();
        const styles = useReflightInfoStyles();

        const departureAirportIata =
            rebookedFlight.departureAirportIata ||
            flightTeam.departureAirportIata;
        const route = rebookedFlight.transitAirportsIata
            ? `${departureAirportIata} - ${rebookedFlight.transitAirportsIata} - ${rebookedFlight.arrivalAirportIata}`
            : `${departureAirportIata} - ${rebookedFlight.arrivalAirportIata}`;

        const departureDate = new Date(rebookedFlight.scheduledDepartureTime);
        const departureDateClassName = isFutureDate(departureDate)
            ? styles.warningValue
            : undefined;

        const arrivalDate = rebookedFlight.scheduledArrivalTime
            ? new Date(rebookedFlight.scheduledArrivalTime)
            : undefined;
        const arrivalDateClassName = isFutureDate(arrivalDate)
            ? styles.warningValue
            : undefined;

        return (
            <div className={mergeClasses(styles.root, className)}>
                <DataRow
                    label={t('CustomerDialog_ReflightInfo')}
                    value={undefined}
                    rootClassName={styles.dataRow}
                    labelClassName={mergeClasses(
                        styles.dataRowLabel,
                        styles.dataRowLabelBold
                    )}
                />
                <DataRow
                    label={`${t('CustomerDialog_FlightNo')}:`}
                    value={rebookedFlight.flightNumber}
                    rootClassName={styles.dataRow}
                    labelClassName={styles.dataRowLabel}
                />
                <DataRow
                    label={`${t('CustomerDialog_Route')}:`}
                    value={route}
                    rootClassName={styles.dataRow}
                    labelClassName={styles.dataRowLabel}
                />
                <DataRow
                    label={`${t('CustomerDialog_DepartureDateTime')}:`}
                    value={formatDateTime(departureDate)}
                    rootClassName={styles.dataRow}
                    labelClassName={styles.dataRowLabel}
                    valueClassName={departureDateClassName}
                />
                {arrivalDate && (
                    <DataRow
                        label={`${t('CustomerDialog_ArrivalDateTime')}:`}
                        value={formatDateTime(arrivalDate)}
                        rootClassName={styles.dataRow}
                        labelClassName={styles.dataRowLabel}
                        valueClassName={arrivalDateClassName}
                    />
                )}
                <DataRow
                    label={`${t('MisconnectedCustomersDialog_Overnight')}:`}
                    value={rebookedFlight.overnightStay ? t('Yes') : t('No')}
                    rootClassName={styles.dataRow}
                    labelClassName={styles.dataRowLabel}
                />
                <DataRow
                    label={`${t('MisconnectedCustomersDialog_Comment')}:`}
                    value={rebookedFlight.notes || '-'}
                    rootClassName={styles.dataRow}
                    labelClassName={styles.dataRowLabel}
                    vertical
                />
            </div>
        );
    }
);

const MisconnectedCustomerInfoDialog = ({
    isFlightTeamBoard,
    open,
    customerInfo,
    onResultClick,
    onOpenChange,
}: CustomerBaggageInfoDialogProps) => {
    const customer = customerInfo?.customer;
    const flightTeam = customerInfo?.flightTeam;

    const { t } = useTranslation();
    const styles = useMisconnectedCustomerInfoDialogStyles();
    const contentStyles = useCustomerUpdatesDialogContentStyles();

    const [rebookedFlight, setRebookedFlight] =
        useState<CustomerRebookedFlight | null>(null);

    useEffect(() => {
        setRebookedFlight(null);

        if (!customer) {
            return;
        }

        fetchRebookedFlight(customer.id).then((x) => setRebookedFlight(x.data));
    }, [customer]);

    const { onNotified, onUnableToNotify } =
        useCustomerUpdatesDialogResultCallbacks(
            'MisconnectedCustomers',
            customer?.id,
            onResultClick
        );

    const content =
        rebookedFlight && flightTeam ? (
            <>
                {rebookedFlight.groupSize > 1 && (
                    <Text
                        weight="semibold"
                        className={styles.groupBookingLabel}
                    >
                        <PeopleCommunity16Regular />
                        {t(
                            'MisconnectedCustomersDialog_GroupBookingTemplate'
                        ).replace(
                            '{count}',
                            rebookedFlight.groupSize.toString()
                        )}
                    </Text>
                )}
                <FlightInfo
                    rebookedFlight={rebookedFlight}
                    flightTeam={flightTeam}
                    className={contentStyles.content}
                />
            </>
        ) : undefined;

    const notificationStatus = rebookedFlight?.notificationInfo.status;

    return (
        <CustomerUpdatesDialog
            open={open}
            customer={customer}
            status={notificationStatus}
            icon={<PassengerIcon />}
            isFlightTeamBoard={isFlightTeamBoard}
            content={content}
            onOpenChange={onOpenChange}
            onNotified={onNotified}
            onUnableToNotify={onUnableToNotify}
        />
    );
};

export default MisconnectedCustomerInfoDialog;
